/**
 * Shared feature flag evaluation logic used by both the frontend and backend.
 * Single source of truth for `hasFeatureAccess`, `isFeatureEligible`, `getFeatureState`.
 * Update when evaluation rules change (e.g. new override sources, rollout strategies).
 */

import { getFeatureFlag } from './featureFlags.js';
import type { FeatureFlag } from './featureFlags.js';
import type { RoleId } from './roles.js';

export interface FeatureFlagUser {
  readonly role: RoleId;
  /**
   * Per-user overrides. Accepts a Mongoose `Map` (server-side) or a plain
   * record (JSON-serialized on the wire / auth context in React).
   */
  readonly featureFlags?:
    | Map<string, boolean>
    | Readonly<Record<string, boolean>>;
}

export type FeatureStateSource =
  | 'invalid'
  | 'not_found'
  | 'role_ineligible'
  | 'user_enabled'
  | 'user_disabled'
  | 'default_enabled'
  | 'default_disabled';

export interface FeatureState {
  readonly enabled: boolean;
  readonly source: FeatureStateSource;
  readonly eligible: boolean;
  readonly hasOverride: boolean;
}

/** Reasons a bulk-update row can be skipped. Exported so clients can switch on them. */
export type BulkUpdateSkipReason =
  | 'user_not_found'
  | 'unknown_feature'
  | 'role_ineligible'
  | 'save_failed'
  | 'duplicate_user';

/**
 * Role eligibility check. A feature without `roles` is role-agnostic (any user
 * can be granted the override); this is how "gate for specific users regardless
 * of role" works at the schema level.
 */
export const isRoleEligible = (
  feature: FeatureFlag,
  user: FeatureFlagUser
): boolean => {
  if (!feature.roles) return true;
  return (feature.roles as readonly string[]).includes(user.role);
};

export const hasUserOverride = (
  user: FeatureFlagUser,
  featureId: string
): boolean => {
  const flags = user.featureFlags;
  if (!flags) return false;
  if (flags instanceof Map) return flags.has(featureId);
  return Object.prototype.hasOwnProperty.call(flags, featureId);
};

export const getUserOverride = (
  user: FeatureFlagUser,
  featureId: string
): boolean | undefined => {
  const flags = user.featureFlags;
  if (!flags) return undefined;
  if (flags instanceof Map) return flags.get(featureId);
  return (flags as Record<string, boolean>)[featureId];
};

/**
 * Checks whether a user has access to a feature, honoring:
 *   1. Invalid inputs → false
 *   2. Unknown feature id → false
 *   3. Explicit override: respected only when the role is eligible.
 *      (The override cannot bypass the role wall when `roles` is defined;
 *      omit `roles` on the flag if you want pure per-user gating.)
 *   4. Default: role eligible AND feature.defaultEnabled.
 */
export const hasFeatureAccess = (
  user: FeatureFlagUser | null | undefined,
  featureId: string
): boolean => {
  if (!user || !user.role || !featureId) return false;

  const feature = getFeatureFlag(featureId);
  if (!feature) return false;

  const eligible = isRoleEligible(feature, user);

  if (hasUserOverride(user, featureId)) {
    // Strict === true so corrupt/non-boolean overrides fail closed instead of
    // truthy-coercing. Schema enforces Boolean on writes; this is defense-in-depth
    // against any future hydration path that deserializes to non-boolean.
    return getUserOverride(user, featureId) === true && eligible;
  }

  return eligible && feature.defaultEnabled;
};

export const isFeatureEligible = (
  user: FeatureFlagUser | null | undefined,
  featureId: string
): boolean => {
  if (!user || !featureId) return false;
  const feature = getFeatureFlag(featureId);
  if (!feature) return false;
  return isRoleEligible(feature, user);
};

/**
 * Returns the fully-resolved state of a feature for a user, including source of
 * truth (default vs override), eligibility, and whether an override exists.
 * Used by response-builders in `/my-features` and `/user/:userId`.
 */
export const getFeatureState = (
  user: FeatureFlagUser | null | undefined,
  featureId: string
): FeatureState => {
  if (!user || !user.role || !featureId) {
    return {
      enabled: false,
      source: 'invalid',
      eligible: false,
      hasOverride: false,
    };
  }

  const feature = getFeatureFlag(featureId);
  if (!feature) {
    return {
      enabled: false,
      source: 'not_found',
      eligible: false,
      hasOverride: false,
    };
  }

  const eligible = isRoleEligible(feature, user);
  const overridePresent = hasUserOverride(user, featureId);

  if (!eligible) {
    return {
      enabled: false,
      source: 'role_ineligible',
      eligible: false,
      hasOverride: overridePresent,
    };
  }

  if (overridePresent) {
    // Strict === true: non-boolean override values fail closed ("user_disabled").
    const override = getUserOverride(user, featureId) === true;
    return {
      enabled: override,
      source: override ? 'user_enabled' : 'user_disabled',
      eligible: true,
      hasOverride: true,
    };
  }

  return {
    enabled: feature.defaultEnabled,
    source: feature.defaultEnabled ? 'default_enabled' : 'default_disabled',
    eligible: true,
    hasOverride: false,
  };
};

/** Alias preserved for API parity with legacy code. */
export const evaluateFeature = getFeatureState;
